/**
 * Voice Activity Detection using Silero VAD neural network.
 * Press mic once → Silero detects speech start/end → audio sent for transcription.
 * Uses @ricky0123/vad-web which provides 16kHz mono Float32Array output.
 */

import { MicVAD } from '@ricky0123/vad-web';

export interface VADCallbacks {
  onSpeechStart?: () => void;
  onSpeechEnd?: (audio: Float32Array) => void;
  onVADMisfire?: () => void;
}

let micVAD: MicVAD | null = null;
let running = false;
let pendingCallbacks: VADCallbacks | null = null;

export async function startVAD(callbacks: VADCallbacks): Promise<void> {
  if (running) {
    await stopVAD();
  }

  pendingCallbacks = callbacks;

  micVAD = await MicVAD.new({
    // Asset paths — served from frontend/public/vad/
    baseAssetPath: '/vad/',
    onnxWASMBasePath: '/vad/',

    // Use legacy model (1.7MB, well-tested)
    model: 'legacy',

    // Don't start until we call start()
    startOnLoad: false,

    // Tuning for "press once, auto-stop on silence" UX
    positiveSpeechThreshold: 0.6,   // confidence > 0.6 = speech
    negativeSpeechThreshold: 0.35,  // confidence < 0.35 = silence
    redemptionMs: 1200,             // 1.2s grace period for mid-sentence pauses
    preSpeechPadMs: 300,            // capture 300ms before speech onset
    minSpeechMs: 500,               // min 0.5s to count as valid speech

    onSpeechStart: () => {
      console.log('[VAD] Speech started (Silero)');
      pendingCallbacks?.onSpeechStart?.();
    },

    onSpeechEnd: (audio: Float32Array) => {
      console.log('[VAD] Speech ended, samples:', audio.length,
        'duration:', (audio.length / 16000).toFixed(1) + 's');
      // Don't pause — keep listening for next utterance (continuous mode)
      pendingCallbacks?.onSpeechEnd?.(audio);
    },

    onVADMisfire: () => {
      console.log('[VAD] Misfire (speech too short)');
      pendingCallbacks?.onVADMisfire?.();
    },
  });

  await micVAD.start();
  running = true;
  console.log('[VAD] Listening (Silero neural VAD)');
}

/**
 * Stop recording and deliver the audio to the callback.
 * pause() flushes any in-progress speech segment via onSpeechEnd.
 */
export async function stopRecordingAndProcess(): Promise<void> {
  if (!running || !micVAD) return;
  running = false;
  await micVAD.pause();
  await micVAD.destroy();
  micVAD = null;
  console.log('[VAD] Stopped and processed');
}

/**
 * Stop without processing (cancel).
 */
export async function stopVAD(): Promise<void> {
  if (!micVAD) return;
  running = false;
  await micVAD.destroy();
  micVAD = null;
  pendingCallbacks = null;
  console.log('[VAD] Cancelled');
}

/**
 * Pause listening without destroying (used during TTS playback).
 */
export function pauseVAD(): void {
  if (!micVAD || !running) return;
  micVAD.pause();
  console.log('[VAD] Paused (TTS playing)');
}

/**
 * Resume listening after pause (used when TTS finishes).
 */
export async function resumeVAD(): Promise<void> {
  if (!micVAD || !running) return;
  await micVAD.start();
  console.log('[VAD] Resumed listening');
}

export function isVADRunning(): boolean {
  return running;
}
